export type LogLevel = 'debug' | 'info' | 'metric' | 'warn' | 'error'

export interface LogEntry {
  timestamp: string
  level: LogLevel
  component: string
  message: string
  data?: unknown
  duration_ms?: number
}

// ── Error description ────────────────────────────────────────────────────────
// Extract a human-readable message from anything thrown. Supabase
// StorageError/PostgrestError are plain objects (not Error instances),
// and DOM image failures reject with a ProgressEvent — both of which
// stringify uselessly to "[object Object]" / "[object ProgressEvent]".
// This pulls out the real signal so upload failures are diagnosable.
export function describeError(err: unknown): string {
  if (err instanceof Error) return err.message
  if (typeof err === 'string') return err
  if (err && typeof err === 'object') {
    const o = err as Record<string, unknown>
    if (typeof o.message === 'string' && o.message) return o.message
    if (typeof o.error === 'string' && o.error) return o.error
    // DOM Event (e.g. ProgressEvent from img.onerror) — no message field.
    if (typeof o.type === 'string') {
      const target = o.target as { src?: string } | undefined
      return `event:${o.type}${target?.src ? ` (${String(target.src).slice(0, 40)}…)` : ''}`
    }
    try { const j = JSON.stringify(o); if (j && j !== '{}') return j } catch { /* fall through */ }
  }
  return String(err)
}

// ── Circular buffer ──────────────────────────────────────────────────────────

const BUFFER_SIZE = 500
const buffer: LogEntry[] = []

function push(entry: LogEntry) {
  if (buffer.length >= BUFFER_SIZE) buffer.shift()
  buffer.push(entry)
}

export function getLogs(): LogEntry[] {
  return [...buffer]
}

export function getLogsByLevel(level: LogLevel): LogEntry[] {
  return buffer.filter(e => e.level === level)
}

// ── Supabase persistence (warn + error only) ─────────────────────────────────

function safeSerialize(value: unknown): unknown {
  try {
    return JSON.parse(JSON.stringify(value))
  } catch {
    return String(value)
  }
}

async function persist(entry: LogEntry) {
  try {
    // Dynamic import avoids circular dependency and defers until after app init
    const { supabase } = await import('../lib/supabaseClient')
    const { data: { session } } = await supabase.auth.getSession()
    await supabase.from('app_logs').insert({
      level: entry.level,
      component: entry.component,
      message: entry.message,
      data: entry.data != null ? safeSerialize(entry.data) : null,
      duration_ms: entry.duration_ms ?? null,
      user_id: session?.user?.id ?? null,
    })
  } catch {
    // Never let logging break the app
  }
}

// ── Core log function ────────────────────────────────────────────────────────

function log(
  level: LogLevel,
  component: string,
  message: string,
  data?: unknown,
  duration_ms?: number,
) {
  const entry: LogEntry = {
    timestamp: new Date().toISOString(),
    level,
    component,
    message,
    ...(data !== undefined && { data }),
    ...(duration_ms !== undefined && { duration_ms }),
  }

  push(entry)

  if (import.meta.env.DEV) {
    const fn =
      level === 'error'  ? console.error :
      level === 'warn'   ? console.warn  :
      level === 'debug'  ? console.debug :
      level === 'metric' ? console.info  :
                           console.log
    const durationSuffix = duration_ms != null ? ` (${duration_ms}ms)` : ''
    fn(`[${level.toUpperCase()}] [${component}] ${message}${durationSuffix}`, ...(data !== undefined ? [data] : []))
  }

  if (level === 'warn' || level === 'error' || level === 'metric') {
    persist(entry)
  }
}

// ── Public API ───────────────────────────────────────────────────────────────

export const logger = {
  debug: (component: string, message: string, data?: unknown) =>
    log('debug', component, message, data),

  info: (component: string, message: string, data?: unknown) =>
    log('info', component, message, data),

  metric: (component: string, message: string, data?: unknown, duration_ms?: number) =>
    log('metric', component, message, data, duration_ms),

  warn: (component: string, message: string, data?: unknown) =>
    log('warn', component, message, data),

  error: (component: string, message: string, data?: unknown) =>
    log('error', component, message, data),

  /**
   * Start a timed operation. Call .done() on success or .fail(err) on failure.
   *
   * const t = logger.time('usePhotos', 'upload', { fileSize })
   * t.done({ photoId })   // logs info with duration_ms
   * t.fail(err)           // logs error with duration_ms
   */
  time(component: string, message: string, data?: Record<string, unknown>) {
    const start = performance.now()
    return {
      done(extra?: Record<string, unknown>) {
        log('info', component, message, { ...data, ...extra }, Math.round(performance.now() - start))
      },
      fail(err: unknown, extra?: Record<string, unknown>) {
        log('error', component, `${message} failed`, {
          ...data,
          ...extra,
          error: describeError(err),
        }, Math.round(performance.now() - start))
      },
    }
  },
}
